import React, { Component } from 'react';
import PreLoader from 'components/PreLoader/PreLoader.react';
import Toolbar from 'components/Toolbar/Toolbar.react';
import Button from 'components/Button/Button.react';
import ChromeDropdown from 'components/ChromeDropdown/ChromeDropdown.react';
import Notification from 'components/Notification/Notification.react';
import Field from 'components/Field/Field.react';
import BoxFieldset from 'components/BoxFieldset/BoxFieldset.react';
import Fieldset from 'components/Fieldset/Fieldset.react';
import JsonPrinter from 'components/JsonPrinter/JsonPrinter.react';
import Label from 'components/Label/Label.react';
import Modal from 'components/Modal/Modal.react';
import CodeSnippet from 'components/CodeSnippet/CodeSnippet.react';
import Parse from 'parse';
import TextInput from 'components/TextInput/TextInput.react';
import Toggle from 'components/Toggle/Toggle.react';
import request from './request';
import generateCurl from './generateCurl';

import styles from './RESTConsole.scss';

class RESTConsole extends Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      method: 'GET',
      endpoint: '',
      useMasterKey: false,
      runAsIdentifier: '',
      sessionToken: null,
      parameters: '',
      response: { results: [] },
      fetchingUser: false,
      inProgress: false,
      error: false,
      curlModal: false,
    };

    this.handleKey = this.handleKey.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
  }

  componentDidMount() {
    document.body.addEventListener('keydown', this.handleKey);
    this.setState({
      loading: false,
    });
  }

  componentWillUnmount() {
    document.body.removeEventListener('keydown', this.handleKey);
  }

  onCloseClick() {
    this.setState({ curlModal: false });
  }

  handleKey(e) {
    const keyCode = e.keyCode || e.which;
    switch (keyCode) {
      case 27:
        this.onCloseClick(this);
        break;
      default:
    }
  }

  showCurl() {
    this.setState({ curlModal: true });
  }

  fetchUser() {
    if (this.state.runAsIdentifier.length === 0) {
      this.setState({ error: false, sessionToken: null });
      return;
    }
    Parse.Query.or(
      new Parse.Query(Parse.User).equalTo('username', this.state.runAsIdentifier),
      new Parse.Query(Parse.User).equalTo('objectId', this.state.runAsIdentifier),
    ).first({ useMasterKey: true }).then((found) => {
      if (found) {
        if (found.getSessionToken()) {
          this.setState({ sessionToken: found.getSessionToken(), error: false, fetchingUser: false });
        } else {
          // Check the Sessions table
          new Parse.Query(Parse.Session).equalTo('user', found).first({ useMasterKey: true }).then((session) => {
            if (session) {
              this.setState({ sessionToken: session.getSessionToken(), error: false, fetchingUser: false });
            } else {
              this.setState({ error: 'Unable to find any active sessions for that user.', fetchingUser: false });
            }
          }, () => {
            this.setState({ error: 'Unable to find any active sessions for that user.', fetchingUser: false });
          });
        }
      } else {
        this.setState({ error: 'Unable to find that user.', fetchingUser: false });
      }
    }, () => {
      this.setState({ error: 'Unable to find that user.', fetchingUser: false });
    });
    this.setState({ fetchingUser: true });
  }

  makeRequest() {
    const endpoint = this.state.endpoint + (this.state.method === 'GET' ? `?${this.state.parameters}` : '');
    const payload = (this.state.method === 'DELETE' || this.state.method === 'GET') ? null : this.state.parameters;
    const options = {};
    if (this.state.useMasterKey) {
      options.useMasterKey = true;
    }
    if (this.state.sessionToken) {
      options.sessionToken = this.state.sessionToken;
    }
    request(
      this.props.currentApp,
      this.state.method,
      endpoint,
      payload,
      options,
    ).then((response) => {
      this.setState({ response });
      document.body.scrollTop = 540;
    });
  }

  render() {
    const methodDropdown = (
      <ChromeDropdown
        value={this.state.method}
        options={['GET', 'POST', 'PUT', 'DELETE']}
        onChange={method => this.setState({ method })} />
    );

    const hasError = this.state.fetchingUser || this.state.endpoint.length === 0 || (this.state.runAsIdentifier.length > 0 && !this.state.sessionToken);

    let parameterPlaceholder = 'where={"username":"johndoe"}';

    if (this.state.method === 'POST' || this.state.method === 'PUT') {
      parameterPlaceholder = '{"name":"John"}';
    }

    let modal = null;
    if (this.state.curlModal) {
      const payload = this.state.method === 'DELETE' ? null : this.state.parameters;
      const options = {};

      if (this.state.useMasterKey) {
        options.useMasterKey = true;
      }

      if (this.state.sessionToken) {
        options.sessionToken = this.state.sessionToken;
      }

      const content = generateCurl(
        this.props.currentApp,
        this.state.method,
        this.state.endpoint,
        payload,
        options,
      );

      modal = (
        <Modal
          title="cURL Request"
          subtitle="Use this to replicate the request"
          icon="laptop-outline"
          customFooter={
            <div className={styles.footer}>
              <Button primary value="Close" onClick={this.onCloseClick.bind(this)} />
            </div>
          }>
          <div className={styles.curl}>
            <CodeSnippet source={content} fullPage={false} language="bash" copy />
          </div>
        </Modal>
      );
    }

    return (
      <PreLoader loading={this.state.loading}>
        <Toolbar section="API Console" subsection="REST Console" />
        <div className={styles.holder}>
          <BoxFieldset el="apps__api_console">
            <div className={styles.settingsBox}>
              <Field
                className="oneliner"
                label={<Label text="Use Master Key?" className="oneliner" description="This will bypass any ACL/CLPs." />}
                input={<Toggle value={this.state.useMasterKey} onChange={useMasterKey => this.setState({ useMasterKey })} />} />

              <div className={styles.hr} />

              <div className={styles.halfHolder}>
                <Field
                  className="half"
                  label={<Label text="What type of request?" />}
                  input={methodDropdown} />

                <Field
                  className="half"
                  label={<Label text="Run as..." description="Send your query as a specific user. You can use their username or Object ID." />}
                  input={<TextInput value={this.state.runAsIdentifier} monospace placeholder="Username or ID" onChange={runAsIdentifier => this.setState({ runAsIdentifier })} onBlur={this.fetchUser.bind(this)} />} />
              </div>


              <Field
                label={<Label text="Which endpoint?" />}
                input={<TextInput value={this.state.endpoint} monospace placeholder="classes/_User" onChange={endpoint => this.setState({ endpoint })} />} />


              {
                this.state.method !== 'DELETE' && (
                  <>
                    <div className={styles.hr} />
                    <Field
                      label={<Label text="Query parameters" />}
                      input={<TextInput value={this.state.parameters} monospace multiline height={80} placeholder={parameterPlaceholder} onChange={parameters => this.setState({ parameters })} />} />
                  </>
                )
              }

              <div className={styles.buttonHolder}>
                <Button disabled={hasError} value="Export to cURL" color="blue" onClick={this.showCurl.bind(this)} />
                <Button primary disabled={hasError} value="Send Query" color="green" progress={this.state.inProgress} onClick={this.makeRequest.bind(this)} />
              </div>
            </div>
          </BoxFieldset>

          <Fieldset
            legend="Results"
            description="">
            <div className={styles.settingsBox}>
              <div className={styles.code}>
                <JsonPrinter object={this.state.response} />
              </div>
            </div>
          </Fieldset>
        </div>
        {modal}
        <Notification note={this.state.error} close />
      </PreLoader>
    );
  }
}

export default RESTConsole;
